import ga4 from 'react-ga4';

// see for initializaton and pageview code: ../App.tsx
// examples from https://github.com/codler/react-ga4

declare global {
  interface Window {
    gtag?: (command: string, eventName: string, params: Record<string, string>) => void;
  }
}

// // Send pageview with a custom path
// ReactGA.send({ hitType: "pageview", page: "/my-path", title: "Custom Title" });

// // Send a custom event
// ReactGA.event({
//   category: "your category",
//   action: "your action",
//   label: "your label", // optional
//   value: 99, // optional, must be a number
//   nonInteraction: true, // optional, true/false
//   transport: "xhr", // optional, beacon/xhr/image
// });

export interface GaEvent {
  action: string;
  category: string;
  label: string;
  value?: number;
  nonInteraction?: boolean;
}

const recordGA4Event = (gaEvent: GaEvent): void => {
  if (!ga4 || !ga4.isInitialized) return;

  const eventCategory = gaEvent.category || 'not specified';
  const eventAction = gaEvent.action || 'not specified';
  const eventLabel = gaEvent.label || 'not specified';

  console.debug('GA4 Event: ', eventAction);

  const eventParams = {
    dt_event_type: eventCategory,
    dt_event_id: eventAction,
    dt_event_detail: eventLabel,
    data_tool_name: 'nqt'
  };

  if (window.gtag) {
    window.gtag('event', 'datatools_click', eventParams);
  }
};


const onGA4TakeTest = (testTakerType: string): void => {
  recordGA4Event({
    action: 'Launch Test',
    category: 'Test Activity',
    label: testTakerType
  });
};

const onGA4TakeQuiz = (subject: string): void => {
  recordGA4Event({
    action: 'Take Quiz',
    category: 'Test Activity',
    label: subject
  });
};

const onGA4LaunchedTicket = (): void => {
  recordGA4Event({
    action: 'Launched Ticket',
    category: 'Test Activity',
    label: 'Launched Ticket'
  });
};


const onGA4DownloadContent = (selectedFormat: string): void => {
  recordGA4Event({
    action: 'Download Content',
    category: 'Product',
    label: selectedFormat
  });
};

const onGA4PublishPaperTest = (hasRoster: boolean): void => {
  recordGA4Event({
    action: 'Publish Test',
    category: 'Product',
    label: hasRoster ? 'Has Roster' : 'No Roster'
  });
};


const onGA4ModifyPerformanceData = (selectedOutput: string): void => {
  recordGA4Event({
    action: 'Modify Performance Data',
    category: 'Selections',
    label: selectedOutput
  });
};


const onGA4PerformQuery = (keyWord: string): void => {
  recordGA4Event({
    action: 'Question Description Search',
    category: 'Query',
    label: keyWord
  });
};

const onGA4CreateRoster = (): void => {
  recordGA4Event({
    action: 'Create Roster',
    category: 'Test Activity',
    label: ''
  });
};

const onGA4ItemDetailNavigation = (label: string, event?: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
  if (event && event?.currentTarget?.classList.contains('active')) return;
  recordGA4Event({
    action: 'Item Detail Navigation',
    category: 'Selections',
    label: label
  });
};

const onGA4GetQuestions = (subject: string | undefined): void => {
  recordGA4Event({
    action: 'Get Questions',
    category: 'Query',
    label: subject || ''
  });
};

const onGA4UserLogin = (): void => {
  recordGA4Event({
    action: 'User Login',
    category: 'Authentication',
    label: 'User Login Successful'
  });
};
const onGA4ForgotPassword = (): void => {
  recordGA4Event({
    action: 'Forgot Password',
    category: 'Authentication',
    label: ''
  });
};

const onGA4UserLoginFail = (): void => {
  recordGA4Event({
    action: 'Login Failed',
    category: 'Authentication',
    label: ''
  });
};

const onGA4UserAccountCreated = (): void => {
  recordGA4Event({
    action: 'Account Created',
    category: 'Authentication',
    label: ''
  });
};

const onGA4OnlineOnly = (): void => {
  recordGA4Event({
    action: 'Online Only',
    category: 'Selections',
    label: ''
  });
};

const onGA4ShareLink = (shareType: string): void => {
  recordGA4Event({
    action: 'Share Link',
    category: 'Test Activity',
    label: shareType
  });
};

const onGA4ShowHideQP = (openClosed: string): void => {
  recordGA4Event({
    action: 'Show Question Panel',
    category: 'Navigation',
    label: openClosed
  });
};

// Show Question List
const onGA4ShowHideQuestions = (openClosed: string): void => {
  recordGA4Event({
    action: 'Show Question List',
    category: 'Navigation',
    label: openClosed
  });
};

const onGA4ResizeQuestionList = (): void => {
  recordGA4Event({
    action: 'Resize Question List',
    category: 'Navigation',
    label: ''
  });
};

const onGA4ViewAddMaterials = (buttonName: string): void => {
  recordGA4Event({
    action: 'Additional Materials',
    category: 'Navigation',
    label: buttonName
  });
};

// Account Deleted
const onGA4DeleteAccount = (): void => {
  recordGA4Event({
    action: 'Account Deleted',
    category: 'Authentication',
    label: ''
  });
};

const onGA4CreateStudentReport = (): void => {
  recordGA4Event({
    action: 'Create Student Report',
    category: 'Product',
    label: ''
  });
};

const onGA4PrintStudentReport = (): void => {
  recordGA4Event({
    action: 'Print Student Report',
    category: 'Product',
    label: ''
  });
};

const onGA4DeleteRoster = (): void => {
  recordGA4Event({
    action: 'Delete Roster',
    category: 'Test Activity',
    label: ''
  });
};

const onGA4DeleteStudentFromRoster = (): void => {
  recordGA4Event({
    action: 'Delete Student from Roster',
    category: 'Test Activity',
    label: ''
  });
};

const gaEvents = {
  recordGA4Event,
  onGA4OnlineOnly,
  onGA4ShareLink,
  onGA4ShowHideQP,
  onGA4ShowHideQuestions,
  onGA4CreateStudentReport,
  onGA4PrintStudentReport,
  onGA4DeleteRoster,
  onGA4DeleteStudentFromRoster,
  onGA4DeleteAccount,
  onGA4ResizeQuestionList,
  onGA4TakeTest,
  onGA4TakeQuiz,
  onGA4ViewAddMaterials,
  onGA4LaunchedTicket,
  onGA4DownloadContent,
  onGA4PublishPaperTest,
  onGA4ModifyPerformanceData,
  onGA4PerformQuery,
  onGA4UserLogin,
  onGA4CreateRoster,
  onGA4ItemDetailNavigation,
  onGA4GetQuestions,
  onGA4ForgotPassword,
  onGA4UserLoginFail,
  onGA4UserAccountCreated,
};

export default gaEvents;




// GA4 automatically records navigation events, so this is not needed
// the event looks like this:
// Event parameters:
// en: click
// `ep.link_id:
// `ep.link_classes: nqt-homepage--enqt-btn
// `ep.link_url: http://poseidonaws.ets-naepanalysis-prod.c.ets.org/takenaepnow_dev
// `ep.link_domain: poseidon.research.ets.org
// `ep.outbound: true
// `_et: 4236

// const onGA4NavigateToTakeNaepNow = (): void => {
//   recordGA4Event({
//     action: 'click',
//     category: 'button',
//     label: 'Take NAEP Now'
//   });
// };


